import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DimensionsService from "services/DimensionsService"
import Dimension from "models/Dimension";
import DimensionMain from "./DimensionMain"
import DimensionInfo from "./DimensionInfo"
import DimensionMap from "components/map/DimensionMap"
import AccountPage from "../AccountPage"
import DimensionHeader from "./DimensionHeader";
import DimensionCamera from "./DimensionCamera";
import AuthContext from 'store/AuthContext';
import { Tooltip } from 'react-tooltip';
import { IoHomeOutline, IoHomeSharp } from "react-icons/io5";
import { BsInfoCircle, BsFillInfoCircleFill } from 'react-icons/bs';
import { IoPersonOutline, IoPersonSharp } from "react-icons/io5";
import { IoMap, IoMapOutline } from "react-icons/io5";
import { useLanguage } from './LanguageContext';
import DimensionFooter from "./DimensionFooter";

import "./DimensionPage.css"

export enum Tab {
    Main = 'main',
    Info = 'info',
    Map = 'map',
    Account = 'account'
};

function tabFromStr(str: string | null): Tab {
    if (!str) {
        return Tab.Main;
    }
    return (Object.values(Tab) as string[]).includes(str) ? (str as Tab) : Tab.Main;
}

const DimensionPage = () => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const authCtx = React.useContext(AuthContext)
    const [searchParams, setSearchParams] = useSearchParams();
    const dimId = searchParams.get("dim");
    const [dimension, setDimension] = React.useState<Dimension | null>(null);

    let navigate = useNavigate();
    const [tab, setTab] = React.useState<Tab>(tabFromStr(searchParams.get("tab")));
    const [mainReloadKey, setMainReloadKey] = React.useState<number>(0)
    
    const [isTooltipVisible, setIsTooltipVisible] = React.useState<boolean>(false);

    const setContainerHeight = (tab: Tab) => {
        if (!containerRef.current) {
            return;
        }

        if (tab == Tab.Main || tab == Tab.Map) {
            const computedStyle = getComputedStyle(document.documentElement);
            const smallScreenSz = parseFloat(computedStyle.getPropertyValue('--small-screen').trim());
            const mobileHeaderSz = parseFloat(computedStyle.getPropertyValue('--dx-mobile-header-height').trim());

            const top = containerRef.current.getBoundingClientRect().top;
            const wh = window.innerHeight;
            const ww = window.innerWidth;
            const availableHeight = wh - top - (ww > smallScreenSz ? 0 : mobileHeaderSz);
            containerRef.current.style.height = `${availableHeight}px`;
        } else {
            containerRef.current.style.height = 'auto';
        }
      };

    function getTab() {
        // a function call to prevent container ref interval callback from caching tab value
        return tab;
    }

    const resizeCallback = () => {
        setContainerHeight(getTab())
    }

    React.useEffect(() => {
        authCtx.logAnalyticsEvent('DimensionWebVisit', {dimension: dimId})
        if (dimId == '1565156516') {
            const hasShownTooltip = window.localStorage.getItem('hasShownTooltip');
            if (!hasShownTooltip) {
                setIsTooltipVisible(true)
                window.localStorage.setItem('hasShownTooltip', 'yes');
            }
        }

        setContainerHeight(tab);

        // Check manually because useEffect(.., [containerRef]) doesn't work
        // I suspect because it renders google map inside and something breaks or times out
        const interval = setInterval(() => {
            if (containerRef.current) {
              console.log('dimension-container is initialized:', containerRef.current);
              setContainerHeight(getTab());
              clearInterval(interval);
            }
          }, 100); // Check every 100 milliseconds
      
          return () => {
            clearInterval(interval);
          };
    }, []);

    React.useEffect(() => {
        setContainerHeight(tab);
    }, [containerRef]); // doesn't work for some reasons

    React.useEffect(() => {
        setContainerHeight(tab);

        // Re-subscribe every time to always have up-to-date tab value
        // This doesn't work in useEffect(.., [])
        window.addEventListener('resize', resizeCallback);
        return () => {
          window.removeEventListener('resize', resizeCallback);
        };
    }, [tab]);

    React.useEffect(() => {
        document.title=`DimensionX | Real World Metaverse`;
        if (dimId) {
            DimensionsService.getDimension('prod', dimId)
                .then(dim => {applyDimension(dim)})
                .catch(err => console.error(`Failed to get dimension [${dimId}]: ${err}`));
        }
    }, [dimId]);

    React.useEffect(() => {
        setTab(tabFromStr(searchParams.get("tab")))
    }, [dimension]);

    function applyDimension(dim: Dimension | null) {
        if (dim) {
            document.title=`DimensionX | ${dim.name}`;
        }
        setDimension(dim)
    }

    function goBackHandler(event: React.MouseEvent<HTMLDivElement>) {
        event.preventDefault();
        navigate(-1);
    };

    const onTabClick = (tab: Tab) => {
        setIsTooltipVisible(false)
        setTab(tab)
        if (tab == Tab.Main) {
            setMainReloadKey(Math.random()); // force reload main tab
        }
        window.postMessage({ type: 'DIMENSION_NAV_BAR_UPDATE', title: null }, '*');
        
    }

    if (!dimension) {
        return (<></>);
    }

    return (
        <div className="dimension-container" ref={containerRef}>

            <DimensionHeader 
                dimId={dimension.id}
                title={dimension.name}
                onBack={goBackHandler}
            />

            <div className="dimension-body">
                {tab == Tab.Main && (<DimensionMain dimension={dimension} key={mainReloadKey}/>)}
                {tab == Tab.Info && (<DimensionInfo dimension={dimension}/>)}
                {tab == Tab.Map && (<DimensionMap dimensionId={dimension.id}/>)}
                {tab == Tab.Account && (<AccountPage/>)}
            </div>

            <DimensionFooter
                dimId={dimension.id}
                active={tab}
                onChange={(item: string) => {
                    onTabClick(tabFromStr(item));
                    //searchParams.set('tab', tabToStr(item)); // doesn't work
                    //setSearchParams({tab: tabToStr(item)}); // overwrites other params
                }}
            />
        </div>
    );
};

export default DimensionPage;
